import React, { useState, useRef, useEffect } from 'react';
import { useFilters } from '../context/FilterContext';
import { API_BASE } from '../services/apiService';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';

const SUGGESTIONS = [
  "What is the current ICR?",
  "Which hospital has the highest claims?",
  "Show me top 5 employees by utilization",
  "What is the cashless vs reimbursement split?",
  "How many maternity claims are there?",
  "What is the rejection rate?",
  "Which ailment has the most claims?",
  "What is the average settlement TAT?",
  "Which relation group drives the most cost?",
  "Any employees exceeding sum insured?",
];

const Chatbot = () => {
  const { filters, drillFilters } = useFilters();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: "Hi! I'm Pazcare Claims Assistant. Ask me anything about your claims data." },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEnd = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (question) => {
    if (!question.trim()) return;

    const userMsg = { role: 'user', text: question.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const filterPayload = {
        date_from: filters.dateRange?.start || null,
        date_to: filters.dateRange?.end || null,
        claim_type: filters.claimType !== 'All' ? filters.claimType : null,
        claim_status: filters.claimStatus !== 'All' ? filters.claimStatus : null,
        relation: filters.relation !== 'All' ? filters.relation : null,
        city_type: filters.cityType !== 'All' ? filters.cityType : null,
        employee_id: filters.employeeId || null,
        sum_insured: filters.sumInsured !== 'All' ? filters.sumInsured : null,
        drill: Object.entries(drillFilters || {}).length > 0
          ? Object.entries(drillFilters).map(([k, v]) => `${k}:${v}`).join(',')
          : null,
      };

      // Extract job_id from URL
      const jobId = window.location.pathname.includes('/dashboard/')
        ? window.location.pathname.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || ''
        : '';

      // Build conversation history from current session messages (last 3 Q&A pairs)
      const history = [];
      const allMsgs = [...messages, userMsg];
      for (let i = 0; i < allMsgs.length - 1; i++) {
        if (allMsgs[i].role === 'user' && allMsgs[i + 1]?.role === 'bot') {
          history.push({ question: allMsgs[i].text, answer: allMsgs[i + 1].text });
        }
      }

      const res = await fetch(`${API_BASE}/api/chat${jobId ? `?job_id=${jobId}` : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim(), filters: filterPayload, history: history.slice(-3) }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'bot', text: data.answer || 'No response.' }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'bot', text: `Error: ${e.message}` }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const [showHint, setShowHint] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  // Show hint bubble after 2 seconds on first load
  useEffect(() => {
    if (open || hintDismissed) return;
    const timer = setTimeout(() => setShowHint(true), 2000);
    return () => clearTimeout(timer);
  }, [open, hintDismissed]);

  // Auto-dismiss hint after 8 seconds
  useEffect(() => {
    if (!showHint) return;
    const timer = setTimeout(() => { setShowHint(false); setHintDismissed(true); }, 8000);
    return () => clearTimeout(timer);
  }, [showHint]);

  if (!open) {
    return (
      <div className="fixed bottom-6 right-6 z-50 print:hidden" data-tour="chatbot">
        {/* Hint bubble */}
        {showHint && (
          <div className="absolute bottom-2 right-16 mr-2" style={{animation: 'fadeInUp 0.4s ease-out'}}>
            <div className="bg-gray-800 text-white px-4 py-3 rounded-xl shadow-lg w-[260px] relative">
              <button onClick={(e) => { e.stopPropagation(); setShowHint(false); setHintDismissed(true); }} className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-gray-600 text-white rounded-full flex items-center justify-center text-[8px] hover:bg-gray-500">✕</button>
              <p className="text-[11px] font-medium leading-relaxed">💬 Chat with your claims data!</p>
              <p className="text-[10px] text-gray-300 mt-1 leading-relaxed">Ask questions like "What is the ICR?", "Top hospitals by amount", or "Claims with TAT &gt; 20 days"</p>
            </div>
            <div className="w-3 h-3 bg-gray-800 rotate-45 absolute top-1/2 -translate-y-1/2 -right-1.5" />
          </div>
        )}
        {/* Chat button with pulse ring */}
        <button
          onClick={() => { setOpen(true); setShowHint(false); setHintDismissed(true); }}
          className="relative w-14 h-14 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all flex items-center justify-center hover:scale-110"
          title="Ask about your claims data"
        >
          {!hintDismissed && <span className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-40" />}
          <MessageCircle size={24} className="relative" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 print:hidden">
      {/* Header */}
      <div className="bg-purple-600 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot size={18} />
          <div>
            <p className="text-sm font-semibold">Pazcare Claims Assistant</p>
            <p className="text-[10px] text-purple-200">Ask anything about your claims data</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="hover:bg-purple-500 rounded-full p-1 transition-colors">
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{overscrollBehavior: 'contain'}}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-purple-100' : 'bg-gray-100'}`}>
                {msg.role === 'user' ? <User size={12} className="text-purple-600" /> : <Bot size={12} className="text-gray-600" />}
              </div>
              <div className={`px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-purple-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}>
                {msg.text}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <Bot size={12} className="text-gray-600" />
            </div>
            <div className="bg-gray-100 px-3 py-2 rounded-xl rounded-bl-sm">
              <Loader2 size={14} className="animate-spin text-purple-600" />
            </div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Suggestions (show when few messages) */}
      {messages.length <= 2 && !loading && (
        <div className="px-4 pb-2">
          <p className="text-[10px] text-gray-400 mb-1">Try asking:</p>
          <div className="flex flex-wrap gap-1">
            {SUGGESTIONS.slice(0, 4).map((s, i) => (
              <button
                key={i}
                onClick={() => sendMessage(s)}
                className="px-2 py-1 text-[10px] bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your claims data..."
            className="flex-1 text-xs border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
            disabled={loading}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
